import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Divider, Tooltip, useTheme } from '@mui/material';
import { useShareDrawerStore, useShiftSeriesEditStore } from '@libs/store/shifts';
import { useNotification } from '@libs/snackbar';
import { Button, ButtonProps, LoadingButton } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { Flex } from '@atoms/layout';
import { TerminateDialog } from '@pages/schedule/dialogs';

type PeriodicShiftDrawerControlsProps = {
  hide?: boolean;
  loading?: boolean;
  onClose?: () => void;
  shift?: {
    id: string;
    isShared: boolean;
    isPublished: boolean;
    isTerminated: boolean;
    canShare: boolean;
  } | null;
  localizationSubset?: string;
  onUpdated?: () => void;
};
export function PeriodicShiftDrawerControls({
  hide,
  onClose,
  shift,
  loading,
  localizationSubset,
  onUpdated,
}: PeriodicShiftDrawerControlsProps) {
  const { showError } = useNotification();
  const theme = useTheme();
  const publish = useShiftSeriesEditStore((s) => s.publish);
  const openShiftShareDrawer = useShareDrawerStore((s) => s.openShiftShareDrawer);
  const disableShare = loading || !shift?.isPublished;
  const [isPublishing, setIsPublishing] = useState(false);
  const onPublishClick = async () => {
    if (!shift) {
      return;
    }
    try {
      setIsPublishing(true);
      await publish(shift.id);
      setIsPublishing(false);
    } catch (error) {
      setIsPublishing(false);
      if (error instanceof Error) {
        showError(error.message);
      }
      return;
    }
    onUpdated?.();
  };

  return (
    <Flex
      column
      position="sticky"
      bottom={0}
      height={hide ? '0' : '70px'}
      overflow="hidden"
      sx={{ transition: 'height 0.3s' }}
    >
      <Divider />
      <Flex gap={2} justifyContent="space-between" p={2} bgcolor={'background.paper'}>
        <Flex>
          <Button variant="passive" onClick={onClose}>
            <FormattedMessage id="common.back" />
          </Button>
        </Flex>
        <Flex gap={2}>
          {shift && !shift.isTerminated && (
            <TerminateButton
              disabled={loading}
              id={shift.id}
              onTerminate={() => onClose?.()}
              localizationSubset={localizationSubset}
            />
          )}
          {shift?.canShare && (
            <Tooltip
              title={
                !shift?.isPublished ? (
                  <FormattedMessage id="shifts.editShiftDrawer.menuButtons.publish.tooltip.publishFirst" />
                ) : (
                  ''
                )
              }
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={disableShare}
                  startIcon={
                    <FaIcon
                      name="share-nodes"
                      color={disableShare ? theme.palette.action.disabled : theme.palette.primary.main}
                    />
                  }
                  onClick={() =>
                    shift &&
                    openShiftShareDrawer({
                      preselectedRecurrences: [shift.id],
                    })
                  }
                >
                  <FormattedMessage id="shifts.shareShiftButton.label" />
                </Button>
              </span>
            </Tooltip>
          )}
          {shift && !shift?.isPublished && (
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={loading}
              onClick={onPublishClick}
              loading={isPublishing}
            >
              <FormattedMessage id={`shifts.editShiftDrawer.menuButtons.publish.label`} />
            </LoadingButton>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}

type TerminateButtonProps = ButtonProps & {
  id: string;
  onTerminate?: () => void;
  localizationSubset?: string;
};
function TerminateButton({ id, onTerminate, localizationSubset, ...buttonProps }: TerminateButtonProps) {
  const { showError, showSuccess } = useNotification();
  const [openTerminate, setOpenTerminate] = useState(false);
  const [loading, setLoading] = useState(false);
  const terminate = useShiftSeriesEditStore((s) => s.terminate);
  const terminateSubmit = async (reason: string) => {
    setLoading(true);
    try {
      await terminate(id, reason);
      setOpenTerminate(false);
      showSuccess(<FormattedMessage id="shifts.terminateDialog.success" />);
      onTerminate?.();
    } catch (e) {
      if (e instanceof Error) showError(e.message);
    }
    setLoading(false);
  };
  return (
    <>
      <Button
        variant="passive"
        onClick={() => setOpenTerminate(true)}
        startIcon={<FaIcon name="xmark" />}
        {...buttonProps}
      >
        <FormattedMessage id={`${localizationSubset}.terminateButtonLabel`} />
      </Button>
      <TerminateDialog
        open={openTerminate}
        onSubmit={terminateSubmit}
        loading={loading}
        type={id.match('shiftseries') ? 'series' : 'rotation'}
        onClose={() => setOpenTerminate(false)}
      />
    </>
  );
}
